/** Testata e stati di caricamento comuni alle viste. */

import type { ReactNode } from 'react';
import { ArrowLeftIcon, OctagonXIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { InTestata } from './inTestata.js';
import { MARGINE_X } from './Corpo.js';
import { useIndietro } from '../navigazione/indietro.js';
import { voce as voceDi } from '../navigazione/voci.js';

/**
 * La stessa testata in ogni vista: titolo, bordo inferiore e, a destra,
 * l'azione principale. Sta fuori dal corpo della vista, che ha il suo
 * padding, perche il bordo e lo sfondo arrivino ai lati. Su telefono i
 * caratteri scendono e le azioni restano in alto a destra: e il titolo ad
 * andare a capo nella sua colonna.
 */
export function Testata({
  titolo,
  voce,
  prima,
  sopra,
  accanto,
  sotto,
  dopo,
  azione,
  indietro,
  fissa = false,
  children,
  className,
}: {
  titolo: ReactNode;
  /** Nelle viste che una voce del menu apre: la sua chiave, per mostrarne l'icona. */
  voce?: string;
  /** Prima del titolo, al posto dell'icona: la maglia nella scheda giocatore. */
  prima?: ReactNode;
  /** Riga piccola sopra il titolo: la data, il contesto. */
  sopra?: ReactNode;
  /** A destra del titolo, sulla stessa riga: i numeri che lo riassumono. */
  accanto?: ReactNode;
  /** Righe piccole sotto il titolo, nella sua colonna. */
  sotto?: ReactNode;
  /**
   * In fondo alla riga del titolo, che resta con lui anche su telefono: il
   * tassello dell'overall nella scheda giocatore.
   */
  dopo?: ReactNode;
  azione?: ReactNode;
  /**
   * Nelle viste senza voce di menu: il pulsante torna alla pagina da cui si
   * e arrivati o, aperta la vista da un link diretto, a questo percorso.
   */
  indietro?: string;
  /**
   * Resta in vista scorrendo, sotto la testata del guscio che su telefono e
   * anch'essa fissa (`--altezza-testata`): nelle viste lunghe da compilare,
   * come appello e voti.
   */
  fissa?: boolean;
  /** Righe sotto il titolo, dentro il bordo: note, comandi della vista. */
  children?: ReactNode;
  className?: string;
}) {
  const Icona = voce ? voceDi(voce).icona : undefined;
  return (
    <header
      className={cn(
        'flex flex-col gap-2 border-b-2 border-foreground bg-testata py-3 @xl:py-4',
        MARGINE_X,
        fissa && 'sticky top-[var(--altezza-testata,0px)] z-20',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {indietro !== undefined && <Indietro ripiego={indietro} />}
          {Icona && <Icona aria-hidden className="size-5 shrink-0 @xl:size-6" />}
          {prima}
          <div className={cn('flex min-w-0 flex-col gap-0.5', dopo && 'flex-1')}>
            {sopra && (
              <div className="text-xs text-muted-foreground first-letter:uppercase @xl:text-[13px]">
                {sopra}
              </div>
            )}
            {accanto ? (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <h1 className={TITOLO}>{titolo}</h1>
                {accanto}
              </div>
            ) : (
              <h1 className={TITOLO}>{titolo}</h1>
            )}
            {sotto}
          </div>
          {dopo}
        </div>
        {azione && <InTestata.Provider value>{azione}</InTestata.Provider>}
      </div>
      {children}
    </header>
  );
}

const TITOLO = 'text-lg leading-tight font-bold @xl:text-[22px]';

function Indietro({ ripiego }: { ripiego: string }) {
  const torna = useIndietro(ripiego);
  return (
    <Button
      variant="ghost"
      size="icon"
      className="-ml-2 shrink-0"
      onClick={torna}
      aria-label="Indietro"
    >
      <ArrowLeftIcon />
    </Button>
  );
}

/** Attesa ed errore di caricamento, uguali in tutte le viste. */
export function StatoCaricamento({ errore }: { errore: string | null }) {
  if (errore) {
    return (
      <Alert variant="destructive">
        <OctagonXIcon />
        <AlertTitle>Caricamento non riuscito</AlertTitle>
        <AlertDescription>{errore}</AlertDescription>
      </Alert>
    );
  }
  return <p className="text-muted-foreground">Carico…</p>;
}
